import BaseApiClient from '../BaseApiClient';
import type {
  ListDataParams,
  ListDataResponse,
  ListExportParams
} from '../types';
import {
  filenameFromContentDisposition,
  isSavingsProductAccrualEnvelope
} from './response-helpers';
import type { SavingsProductAccrualResponse } from '@/interfaces/SavingsProductAccrual.interface';
import type {
  SavingsProduct,
  SavingsProductFormPayload,
  SavingsProductUpdatePayload,
  SavingsProductListItem,
  SavingsProductOptions,
  SavingsProductRate,
  SavingsScheduleProductRatePayload,
  SavingsRollupDetail,
  SavingsRollupListItem
} from '@/interfaces/SavingsProduct.interface';

const VERSION = '2026-01-20';
const IDEMPOTENCY_HEADER = 'Idempotency-Key';

const toFormData = (
  payload: SavingsProductFormPayload,
  method?: 'PUT' | 'PATCH'
): FormData => {
  const formData = new FormData();
  if (method) formData.append('_method', method);

  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        formData.append(key, '');
        return;
      }
      value.forEach((item) => formData.append(`${key}[]`, String(item)));
      return;
    }
    formData.append(key, value ?? '');
  });

  return formData;
};

const withSearch = (
  filters: ListDataParams['filters'],
  searchTerm?: string
): ListDataParams['filters'] => {
  const term = searchTerm?.trim();
  return term ? { ...filters, search: term } : filters;
};

export class ApiSavingsProducts extends BaseApiClient {
  private headers(extra: Record<string, string> = {}) {
    return { headers: { Version: VERSION, ...extra } };
  }

  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsProductListItem>> {
    const res = await this.client.getList(
      '/savings-products',
      { ...listData, filters: withSearch(listData.filters, searchTerm) },
      this.headers()
    );

    return {
      items: res.data?.data ?? [],
      pagination: {
        page: res.data?.meta?.current_page ?? listData.page,
        perPage: res.data?.meta?.per_page ?? listData.perPage,
        total: res.data?.meta?.total ?? 0,
        lastPage: res.data?.meta?.last_page
      }
    };
  }

  async listRollups(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsRollupListItem>> {
    const res = await this.client.getList(
      '/savings-rollups',
      { ...listData, filters: withSearch(listData.filters, searchTerm) },
      this.headers()
    );

    return {
      items: res.data?.data ?? [],
      pagination: {
        page: res.data?.meta?.current_page ?? listData.page,
        perPage: res.data?.meta?.per_page ?? listData.perPage,
        total: res.data?.meta?.total ?? 0,
        lastPage: res.data?.meta?.last_page
      }
    };
  }

  async getRollup(
    id: number,
    listData?: ListDataParams
  ): Promise<SavingsRollupDetail> {
    const res = await this.client.getList(
      `/savings-rollups/${id}`,
      listData,
      this.headers()
    );
    return res.data.data;
  }

  async get(id: number): Promise<SavingsProduct> {
    const res = await this.client.get(
      `/savings-products/${id}`,
      this.headers()
    );
    return res.data.data;
  }

  async scheduleRate(
    id: number,
    payload: SavingsScheduleProductRatePayload
  ): Promise<SavingsProduct> {
    const res = await this.client.post(
      `/savings-products/${id}/rates`,
      payload,
      this.headers()
    );
    return res.data.data;
  }

  async listRates(id: number): Promise<SavingsProductRate[]> {
    const res = await this.client.get(
      `/savings-products/${id}/rates`,
      this.headers()
    );
    return Array.isArray(res.data?.data) ? res.data.data : [];
  }

  async options(): Promise<SavingsProductOptions> {
    const res = await this.client.get(
      '/savings-product-options',
      this.headers()
    );
    return res.data.data;
  }

  /** The idempotency key must be reused when the same submission is retried. */
  async create(
    payload: SavingsProductFormPayload,
    idempotencyKey: string
  ): Promise<SavingsProduct> {
    const res = await this.client.post(
      '/savings-products',
      toFormData(payload),
      this.headers({
        'Content-Type': 'multipart/form-data',
        [IDEMPOTENCY_HEADER]: idempotencyKey
      })
    );
    return res.data.data;
  }

  /** A full editor snapshot. The same idempotency key retries this submission. */
  async createIssue(
    id: number,
    payload: SavingsProductFormPayload,
    idempotencyKey: string
  ): Promise<SavingsProduct> {
    const res = await this.client.post(
      `/savings-products/${id}/issues`,
      toFormData(payload),
      this.headers({
        'Content-Type': 'multipart/form-data',
        [IDEMPOTENCY_HEADER]: idempotencyKey
      })
    );
    return res.data.data;
  }

  async update(
    id: number,
    payload: SavingsProductUpdatePayload
  ): Promise<SavingsProduct> {
    const res = await this.client.patch(
      `/savings-products/${id}`,
      payload,
      this.headers()
    );
    return res.data.data;
  }

  /** PHP only parses multipart bodies on POST, so the PUT is spoofed. */
  async replaceKeyFeatures(
    id: number,
    file: File,
    updatedAt: string | null
  ): Promise<SavingsProduct> {
    const res = await this.client.post(
      `/savings-products/${id}/key-features`,
      toFormData({ key_features: file, updated_at: updatedAt }, 'PUT'),
      this.headers({ 'Content-Type': 'multipart/form-data' })
    );
    return res.data.data;
  }

  async setStatus(
    id: number,
    active: boolean,
    updatedAt: string | null
  ): Promise<SavingsProduct> {
    const res = await this.client.put(
      `/savings-products/${id}/status`,
      { active, updated_at: updatedAt },
      this.headers()
    );
    return res.data.data;
  }

  async delete(id: number): Promise<void> {
    await this.client.delete(`/savings-products/${id}`, this.headers());
  }

  /**
   * The pooled product daily accrual report for a projected Savings product.
   * The API owns every monetary value and the completeness metadata, so the
   * response is passed through unchanged, and a response that does not match
   * the documented envelope is rejected so the modal shows its failure state.
   */
  async listAccruals(
    productId: string | number,
    params: { end_date: string; page?: number; perPage?: number }
  ): Promise<SavingsProductAccrualResponse> {
    const urlParams = new URLSearchParams();
    urlParams.set('filter[end_date]', params.end_date);
    if (params.page !== undefined) {
      urlParams.set('page[number]', String(params.page));
    }
    if (params.perPage !== undefined) {
      urlParams.set('page[size]', String(params.perPage));
    }

    const res = await this.client.get(
      `/savings-products/${productId}/accruals?${urlParams.toString()}`,
      this.headers()
    );
    if (!isSavingsProductAccrualEnvelope(res.data)) {
      throw new Error('Malformed product accruals response');
    }

    return res.data;
  }

  async exportAccounts(id: number): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.getList(
      `/savings-products/${id}/accounts/export`,
      undefined,
      { responseType: 'blob', ...this.headers({ Accept: 'text/csv' }) }
    );

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        `savings-product-${id}-accounts.csv`
      )
    };
  }

  async export(
    listData: ListExportParams,
    searchTerm?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.getList(
      '/savings-products/export',
      {
        filters: withSearch(listData.filters, searchTerm),
        sort: listData.sort
      },
      { responseType: 'blob', ...this.headers({ Accept: 'text/csv' }) }
    );

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        'savings-products.csv'
      )
    };
  }
}

export * from './query-keys';
export * from './use-savings-products-queries';
export * from './use-savings-products-mutations';
